import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import EChartsStockChart, { ChartSeries } from '../../../../components/EChartsStockChart';
import { getRentabilidadeComparativa } from '../../../../services/carteiraDiariaService';
import { RentabilidadeComparativaResponse } from '../../../../types/carteiraDiaria';
import { formatDate } from '../../../../utils/formatters';

const COMP_SERIES = [
  { key: 'fundo' as const, label: 'Fundo',  color: '#2C91AD', defaultOn: true  },
  { key: 'cdi'   as const, label: 'CDI',    color: '#29A152', defaultOn: true  },
  { key: 'ibov'  as const, label: 'IBOV',   color: '#F5A623', defaultOn: false },
  { key: 'ipca'  as const, label: 'IPCA',   color: '#E51739', defaultOn: false },
] as const;

type CompKey = typeof COMP_SERIES[number]['key'];

const toTS = (d: string) => {
  const [y, m, dd] = d.split('-').map(Number);
  return Date.UTC(y, m - 1, dd);
};

export interface RentabilidadeComparativaTabProps {
  fundoId: number;
  isVisible: boolean;
}

const RentabilidadeComparativaTab: React.FC<RentabilidadeComparativaTabProps> = ({ fundoId, isVisible }) => {
  const [rentData, setRentData] = useState<RentabilidadeComparativaResponse | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [visible, setVisible]   = useState<Record<CompKey, boolean>>({
    fundo: true, cdi: true, ibov: false, ipca: false,
  });

  useEffect(() => {
    setLoading(true);
    setError(null);
    getRentabilidadeComparativa(fundoId)
      .then(setRentData)
      .catch(() => setError('Não foi possível carregar rentabilidade comparativa.'))
      .finally(() => setLoading(false));
  }, [fundoId]);

  const toggle = useCallback((key: CompKey) => setVisible(prev => ({ ...prev, [key]: !prev[key] })), []);

  const chartSeries: ChartSeries[] = useMemo(() => {
    if (!rentData) return [];
    return COMP_SERIES
      .filter(s => visible[s.key])
      .map(s => ({
        name: s.label,
        color: s.color,
        data: rentData.dates.map((d, i) => [toTS(d), rentData[s.key][i]] as [number, number]),
      }));
  }, [rentData, visible]);

  if (!isVisible) return null;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error)   return <Typography color="error" sx={{ py: 2 }}>{error}</Typography>;
  if (!rentData || rentData.dates.length === 0)
    return <Typography sx={{ py: 3, color: '#8A98A8', textAlign: 'center', fontSize: '13px' }}>Sem histórico disponível.</Typography>;

  return (
    <Box sx={{ pt: 2 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
        {COMP_SERIES.map(s => (
          <Button
            key={s.key}
            size="small"
            variant={visible[s.key] ? 'contained' : 'outlined'}
            onClick={() => toggle(s.key)}
            sx={{
              fontFamily: 'Inter', fontSize: '12px', textTransform: 'none',
              borderColor: s.color,
              color: visible[s.key] ? '#fff' : s.color,
              backgroundColor: visible[s.key] ? s.color : 'transparent',
              '&:hover': { backgroundColor: visible[s.key] ? s.color : `${s.color}20`, borderColor: s.color },
            }}
          >
            {s.label}
          </Button>
        ))}
      </Stack>
      <Typography variant="caption" sx={{ color: '#8A98A8', mb: 1, display: 'block' }}>
        Retorno acumulado desde {formatDate(rentData.data_inicio)} — valores em %
      </Typography>
      {chartSeries.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 480, color: '#8A98A8', fontSize: '13px', fontFamily: 'Inter' }}>
          Selecione ao menos um indicador para exibir o gráfico.
        </Box>
      ) : (
        <EChartsStockChart series={chartSeries} yAxisTitle="%" suffix="%" height={480} simplifiedXAxis={false} rebasable />
      )}
    </Box>
  );
};

export default RentabilidadeComparativaTab;
